import type { ComposeRibbonControlDefProps, ReadOnlyRibbonControlDefProps, RibbonControlDefinitionProps, } from "./RibbonControlDefinitionProps";
import { type MailRibbonControlId, type MailRibbonTabId } from "owa-ribbon-ids/lib/mailRibbonId";
import type { RibbonControlId, RibbonTabId } from "owa-ribbon-ids";
import { getAccountScopeUserSettings, getUserConfiguration, isConsumer, isPaid, } from "owa-session-store";
import { getMailMenuItemShouldHideForMailbox, getMailMenuItemShouldShow, isBlockSupportedForUnifiedInboxOrMultiAccountSearchSelection, } from "owa-mail-filterable-menu-behavior";
import { type ActionControlId } from "owa-ribbon-ids/lib/actionControlId";
import { type FormatControlId } from "owa-ribbon-ids/lib/formatControlId";
import type { MailboxInfo } from "owa-client-types";
import type { TraceErrorObject } from "owa-analytics-types";
import { type MenuItemType } from "owa-filterable-menu/lib/components/MenuItemType";
import { addinsCapability } from "owa-capabilities-definitions/lib/addinsCapability";
import { serviceRequestCapability } from "owa-capabilities-definitions/lib/serviceRequestCapability";
import { owaComputedFn } from "owa-computed-fn";
import { getCmdIdFromRibbonId } from "owa-mail-ribbon-store/lib/util/mailRibbonIdToCmdIdMap";
import { getIsDarkTheme } from "owa-fabric-theme";
import { getRibbonMailboxInfo } from "./getRibbonMailboxInfo";
import { hasEnterpriseAccount } from "owa-mail-ribbon-store/lib/util/hasEnterpriseAccount";
import { isBrowserChrome, isBrowserEDGECHROMIUM, isBrowserFirefox } from "owa-user-agent";
import { isCapabilityEnabled, isCapabilitySupported } from "owa-capabilities";
import { isCopilotFeatureEnabled } from "owa-copilot-settings-utils";
import isEncryptionEnabled from "owa-encryption-common/lib/utils/isEncryptionEnabled";
import isEncryptionTemplateAvailable from "owa-mail-protection/lib/utils/isEncryptionTemplateAvailable";
import { isGlobalFeatureEnabled } from "owa-feature-flags/lib/utils/isFeatureEnabled";
import { LoopType, isFluidEnabledForSource } from "owa-fluid-validations";
import isGroupComposeViewState from "owa-mail-compose-store/lib/utils/isGroupComposeViewState";
import { isMOS3AppServiceAppsInMailAvailable } from "owa-m365-acquisitions/lib/utils/isMOS3AppServiceAppsInMailAvailable";
import { isMessageExtensionsFlyoutEnabled } from "owa-m365-acquisitions/lib/utils/isMessageExtensionsFlyoutEnabled";
import { isNativeReportingEnabled } from "owa-reporting";
import isPublicFolderComposeViewState from "owa-mail-compose-store/lib/utils/isPublicFolderComposeViewState";
import { learningToolsCapability } from "owa-capabilities-definitions/lib/learningToolsCapability";
import { favoritesCapability } from "owa-capabilities-definitions/lib/favoritesCapability";
import { populateAddinsCapability } from "owa-capabilities-definitions/lib/populateAddinsCapability";
import { shouldHideRibbonControlBasedOnHostApp } from "./hostAppConfig";
import getSelectedTableView from "owa-mail-list-store/lib/utils/getSelectedTableView";
import type TableView from "owa-mail-list-store/lib/store/schema/TableView";
import { getItem } from "./getItem";
import { isItemInFolder } from "./isItemInFolder";
import { getComposeViewState } from "owa-mail-reading-pane-store-conversation/lib/utils/getComposeViewState";
import isMessageRecallEnabled from "owa-cloud-message-recall/lib/utils/isMessageRecallEnabled";
import { isReadingPaneStackedView } from "owa-mail-organization-settings/lib/utils/readingPaneStackedViewUtils";
import isSharedMailboxExplicitLogon from "owa-mail-store/lib/utils/isSharedMailboxExplicitLogon";
import folderIdToName from "owa-session-store/lib/utils/folderIdToName";
import { isSpamAddinInstalled } from "owa-addins-store/lib/utils/isSpamAddinInstalled";
import { getExtensionForAddinId } from "owa-addins-store/lib/store/getExtensionForAddinId";
import { isTeamsChatEnabledWithDefault } from "owa-teams-integration/lib/utils/isTeamsChatEnabledWithDefault";
import { isLocalDataBackedMailboxInfo } from "owa-account-source-list/lib/utils/isLocalDataBackedAccount";
import { isMessageFileMailboxInfo } from "owa-client-types/lib/MessageFileMailboxInfo";
import { isFileActivationProjection } from "./isFileActivationProjection";
import { editorCapability } from "owa-capabilities-definitions/lib/editorCapability";
import getIsLightRPOn from "owa-light-reading-pane-store/lib/selectors/getIsLightRPOn";
import { hasPaidAccount } from "owa-has-enterprise-or-premium-license-store/lib/selectors/hasPaidAccount";
import { getEnterpriseOrPremiumLicenseStatus } from "owa-has-enterprise-or-premium-license-store";
import { getAppliedRefiners, getSuggestedRefiners } from "owa-search-refiners";
import { SearchScenarioId, canToggleSearchIncludeDeleted } from "owa-search-store";
import mailSearchStore from "owa-mail-search/lib/store/store";
import { mailTemplatesCapability } from "owa-capabilities-definitions/lib/mailTemplatesCapability";
import { quickPartsCapability } from "owa-capabilities-definitions/lib/quickPartsCapability";
import { saveEmailAsEMLToFileCapability } from "owa-capabilities-definitions/lib/saveEmailAsEMLToFileCapability";
import { saveEmailAsMSGToFileCapability } from "owa-capabilities-definitions/lib/saveEmailAsMSGToFileCapability";
import { saveEmailAsOFTToFileCapability } from "owa-capabilities-definitions/lib/saveEmailAsOFTToFileCapability";
import { searchFoldersCapability } from "owa-capabilities-definitions/lib/searchFoldersCapability";
import getIsTeamsChatTabEnabled from "owa-tabbed-search/lib/selectors/getIsTeamsChatTabEnabled";
import { isUnifiedInboxMailboxInfo, type UnifiedInboxMailboxInfo, } from "owa-client-types/lib/UnifiedInboxMailboxInfo";
import { SearchProvider } from "owa-search-service/lib/data/schema/SearchProvider";
import { isMultiAccountTable, type TableQueryType } from "owa-mail-list-store";
import type { SearchTableQuery } from "owa-mail-list-search";
import getSearchProvider from "owa-mail-search/lib/selectors/getSearchProvider";
import { shareToTeamsGUIDs } from "owa-addins-surface-actions/lib/utils/addinGUIDConst";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import { getGroupCreationEnabled } from "owa-groups-shared-store/lib/selectors/unifiedGroupsStoreSelectors";
import { advancedFlagCapability } from "owa-capabilities-definitions/lib/advancedFlagCapability";

const sentItemsFolderId = "sentitems";

export function getNewGroupControlState(mailboxInfo: MailboxInfo): {
    shouldShow: boolean;
    disabled: boolean;
} {
    const isGroupCreationEnabled = !!getGroupCreationEnabled(mailboxInfo);
    const shouldShowDisabledControl = !isGroupCreationEnabled && isGlobalFeatureEnabled("grp-disablecreategroup");
    
    return {
        shouldShow: isGroupCreationEnabled || shouldShowDisabledControl,
        disabled: shouldShowDisabledControl,
    };
}

/**
 * This function decides whether a control should show or not.
 * For any logic on show/hiding controls, please put them here.
 *
 * @controlId the current control being tested to see whether it should show
 * @props Optional parameter, used for certain buttons to test whether they should show.
 *        Note: Props is only undefined in Customizer cases. Certain buttons use this logic truth
 *        when determining whether they should show.
 * @isCustomizer set this to true if we are in customizer view
 */
export const shouldShowRibbonControl: (tabId: RibbonTabId, controlId: RibbonControlId, props: RibbonControlDefinitionProps, isCustomizer: boolean) => boolean = owaComputedFn(function shouldShowRibbonControl(tabId: RibbonTabId, controlId: RibbonControlId, props: RibbonControlDefinitionProps, isCustomizer: boolean): boolean {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    if (getMailMenuItemShouldHideForMailbox(getCmdIdFromRibbonId(controlId, mailboxInfo), mailboxInfo)) {
        return false;
    }
    const isUnifiedInbox = isUnifiedInboxMailboxInfo(mailboxInfo);
    
    if (shouldHideRibbonControlBasedOnHostApp(controlId)) {
        return false;
    }
    
    const composeProps = props as ComposeRibbonControlDefProps;
    const isHTML: boolean = composeProps.composeViewState?.bodyType === "HTML";
    const isFileProjection: boolean = isFileActivationProjectionForProps(props);
    const selectedTableView = getSelectedTableView() ?? null;
    const isLightRPOn = getIsLightRPOn();
    
    const composeViewState = getComposeViewState();
    
    switch (tabId) {
        case 1:
            switch (controlId) {
                case 502:
                    return isWin32StylingEnabled() && !props.isPopout;
                
                case 508:
                    return (!isMultiAccountTable(selectedTableView) &&
                        !isGlobalFeatureEnabled("grp-inboxExp-ribbon"));
                
                case 585:
                    return getNewGroupControlState(mailboxInfo).shouldShow;
                
                case 663:
                    return isGlobalFeatureEnabled("grp-inboxExp-ribbon") && hasEnterpriseAccount();
                
                case 529:
                    return (!isMultiAccountTable(selectedTableView) &&
                        shouldShowGetAddins(getIsConsumer(mailboxInfo), 1, mailboxInfo));
                case 7022:
                    return (shouldShowGetM365Apps(mailboxInfo) &&
                        !shouldShowGetAddins(getIsConsumer(mailboxInfo), 1, mailboxInfo));
                case 7016:
                    return isMessageExtensionsFlyoutEnabled(mailboxInfo);
                
                case 550:
                    return (!isMultiAccountTable(selectedTableView) &&
                        (isPaid(mailboxInfo) || hasPaidAccount()) &&
                        mailboxInfo.type != "GroupMailbox" &&
                        !isMultiAccountTable(selectedTableView));
                
                case 658:
                    return isBlockSupportedForUnifiedInboxOrMultiAccountSearchRibbon(isUnifiedInbox, mailboxInfo);
                
                case 657:
                    if (isGlobalFeatureEnabled("tri-promote-not-junk")) {
                        return false;
                    }
                    // Hide the native reporting button if the user has the spam report addin
                    // For unified inbox, check if any account supports native reporting
                    if (isUnifiedInbox) {
                        return isAnyUnifiedInboxAccountReportingEnabled(mailboxInfo);
                    }
                    return (!hasSpamReportAddin(mailboxInfo) && isNativeReportingEnabled(mailboxInfo));
                case 535:
                case 547:
                    return shouldShowStandaloneReportControl(mailboxInfo, selectedTableView);
                
                // Only show these controls if the user has at least one enterprise account
                case 556:
                case 557:
                case 555:
                case 651:
                    return hasEnterpriseAccount();
                
                case 553:
                    return hasEnterpriseAccount() && !shouldShowReplyByMeetingFlyout(mailboxInfo);
                
                case 698:
                case 699:
                case 700:
                    return shouldShowReplyByMeetingFlyout(mailboxInfo);
                
                case 696:
                    return (hasEnterpriseAccount() &&
                        !!isCopilotFeatureEnabled("ReplyByMeeting", mailboxInfo) &&
                        !shouldShowReplyByMeetingFlyout(mailboxInfo));
                
                case 678:
                    return !hasEnterpriseAccount();
                
                // MenuItemType Ribbon_Restore controls whether the restore buttons should be show in the ribbon
                // there are Restore and RestoreAll types as well, which are used to determine if the buttons are enabled
                case 683:
                    return getMailMenuItemShouldShow(81, mailboxInfo);
                case 684:
                    return getMailMenuItemShouldShow(81, mailboxInfo);
                case 558:
                    return !(isUnifiedInbox || mailboxInfo.type === "GroupMailbox");
                case 686:
                    // Show recall message button in Home Tab when the feature is enabled and:
                    // 1. List View is not in Conversation Mode
                    // 2. List View is in Conversation mode and Reading Pane Conversation is disabled
                    return (selectedTableView &&
                        !isReadingPaneStackedView(selectedTableView?.tableQuery?.listViewType) &&
                        getMailMenuItemShouldShow(83, mailboxInfo));
                case 697:
                    return (selectedTableView &&
                        !isReadingPaneStackedView(selectedTableView?.tableQuery?.listViewType) &&
                        getMailMenuItemShouldShow(86, mailboxInfo));
                
                case 731:
                case 702:
                case 703:
                    return (isGlobalFeatureEnabled("mon-teams-ChatAroundEmail") &&
                        isTeamsChatEnabledWithDefault(mailboxInfo));
                
                case 709:
                    return shouldShowShareToTeamsChat(mailboxInfo);
                case 712:
                    return (selectedTableView &&
                        !isReadingPaneStackedView(selectedTableView?.tableQuery?.listViewType) &&
                        getMailMenuItemShouldShow(92, mailboxInfo));
                case 548:
                    return getMailMenuItemShouldShow(58, mailboxInfo);
                case 572:
                    return !isMultiAccountTable(selectedTableView);
                
                default:
                    return true;
            }
        
        case 5:
            switch (controlId) {
                case 620:
                    return shouldShowAttachFile(props, isCustomizer);
                case 529:
                    return shouldShowGetAddins(getIsConsumer(mailboxInfo), 5, mailboxInfo);
                case 7016:
                    return shouldShowGetMessageExtensions(mailboxInfo);
                case 7021:
                    return shouldShowPolls(mailboxInfo);
                case 7000:
                    return showShouldLoopComponent(mailboxInfo);
                case 7011:
                    return shouldShowFindTimeControl(getIsConsumer(mailboxInfo));
                case 7026:
                    return isFluidEnabledForSource("MailCompose", mailboxInfo, LoopType.VideoRecord);
                case 7067:
                    return composeProps.composeViewState.contentPicker.isSymbolsFeatureEnabled;
                case 639:
                    return composeProps.composeViewState.contentPicker.useLocalContentOnly;
                case 7042:
                    return !composeProps.composeViewState.contentPicker.useLocalContentOnly;
                case 7054:
                    return isCapabilityEnabled(quickPartsCapability, mailboxInfo);
                default:
                    return true;
            }
        
        case 4:
            switch (controlId) {
                case 6011:
                    return !isHTML && !isBrowserFirefox();
                case 6012:
                    return isHTML && !isBrowserFirefox();
                default:
                    return true;
            }
        
        case 3:
            switch (controlId) {
                case 647:
                    return !isWin32StylingEnabled();
                case 532:
                    return isCapabilityEnabled(learningToolsCapability, mailboxInfo);
                case 558:
                    return !(isUnifiedInbox || mailboxInfo.type === "GroupMailbox");
                case 687:
                    if (!selectedTableView) {
                        return false;
                    }
                    
                    return (!composeViewState &&
                        isReadingPaneStackedView(selectedTableView.tableQuery?.listViewType));
                case 720:
                    if (!(selectedTableView || composeViewState)) {
                        return false;
                    }
                    
                    return getIsDarkTheme();
                case 701:
                    return !isWin32StylingEnabled() && !isLocalDataBackedMailboxInfo(mailboxInfo);
                case 774:
                    return (isCapabilityEnabled(serviceRequestCapability, mailboxInfo) &&
                        folderIdToName(selectedTableView?.tableQuery?.folderId ?? "") === "inbox");
                default:
                    return true;
            }
        
        case 6:
            switch (controlId) {
                default:
                    return true;
            }
        
        case 7:
            switch (controlId) {
                default:
                    return true;
            }
        
        case 8:
            switch (controlId) {
                case 519:
                case 505:
                case 527:
                case 509:
                case 559:
                case 552:
                    return !isFileProjection;
                case 616:
                    return !isFileProjection && !isMultiAccountTable(selectedTableView);
                case 572:
                    return !isMultiAccountTable(selectedTableView);
                case 658:
                    return (!isFileProjection &&
                        isBlockSupportedForUnifiedInboxOrMultiAccountSearchRibbon(isUnifiedInbox, mailboxInfo));
                case 540:
                    return !isFileProjection;
                case 548:
                    return getMailMenuItemShouldShow(58, mailboxInfo);
                case 550:
                    return (!isMultiAccountTable(selectedTableView) &&
                        (isPaid(mailboxInfo) || hasPaidAccount()) &&
                        mailboxInfo.type != "GroupMailbox" &&
                        !isMultiAccountTable(selectedTableView));
                case 657:
                    if (isGlobalFeatureEnabled("tri-promote-not-junk")) {
                        return false;
                    }
                    // Hide the native reporting button if the user has the spam report addin
                    // For unified inbox, check if any account supports native reporting
                    if (isUnifiedInbox) {
                        return (!isFileProjection &&
                            isAnyUnifiedInboxAccountReportingEnabled(mailboxInfo));
                    }
                    return (!hasSpamReportAddin(mailboxInfo) &&
                        isNativeReportingEnabled(mailboxInfo) &&
                        !isFileProjection);
                case 535:
                case 547:
                    return (!isFileProjection &&
                        shouldShowStandaloneReportControl(mailboxInfo, selectedTableView));
                
                case 678:
                    return !hasEnterpriseAccount();
                
                case 651:
                case 556:
                case 557:
                case 555:
                    return hasEnterpriseAccount();
                
                case 553:
                    return hasEnterpriseAccount() && !shouldShowReplyByMeetingFlyout(mailboxInfo);
                
                case 698:
                case 699:
                case 700:
                    return shouldShowReplyByMeetingFlyout(mailboxInfo);
                
                case 696:
                    return (hasEnterpriseAccount() &&
                        !!isCopilotFeatureEnabled("ReplyByMeeting", mailboxInfo) &&
                        !shouldShowReplyByMeetingFlyout(mailboxInfo));
                
                case 686:
                    return (isMessageRecallEnabled(mailboxInfo) &&
                        shouldShowTrackingControlsOnReadTab(props) &&
                        !isFileProjection);
                
                case 697:
                    return shouldShowTrackingControlsOnReadTab(props) && !isFileProjection;
                
                case 731:
                case 702:
                case 703:
                    return (isGlobalFeatureEnabled("mon-teams-ChatAroundEmail") &&
                        isTeamsChatEnabledWithDefault(mailboxInfo));
                
                case 709:
                    return shouldShowShareToTeamsChat(mailboxInfo);
                
                case 705:
                    return isFileProjection;
                
                case 532:
                    return isCapabilityEnabled(learningToolsCapability, mailboxInfo);
                
                case 712:
                    return shouldShowTrackingControlsOnReadTab(props);
                
                case 7016:
                    return isMessageExtensionsFlyoutEnabled(mailboxInfo);
                
                default:
                    return true;
            }
        
        case 10:
            switch (controlId) {
                case 7069:
                    return shouldShowComposeFollowUp(composeProps);
                case 529:
                    return shouldShowGetAddins(getIsConsumer(mailboxInfo), 10, mailboxInfo);
                case 7016:
                    return shouldShowGetMessageExtensions(mailboxInfo);
                case 7021:
                    return shouldShowPolls(mailboxInfo);
                case 7023:
                    // TODO (ADO390010): Remove CopilotDrafts ActionControlId from Ribbon.
                    // This now always returns false since the main Command Center flights have graduated.
                    // This is a TODO to keep the feature graduation PR scoped and less bloated.
                    return false;
                case 7000:
                    return showShouldLoopComponent(mailboxInfo);
                case 7011:
                    return shouldShowFindTimeControl(getIsConsumer(mailboxInfo));
                case 564:
                    return shouldShowSensitivity(props);
                case 7012:
                    return shouldShowEncrypt(props);
                case 6011:
                    return !isHTML && !isBrowserFirefox();
                case 6012:
                    return isHTML && !isBrowserFirefox();
                case 7010:
                    return !getIsConsumer(mailboxInfo);
                case 7003:
                    return getIsDarkTheme() && !isLightRPOn;
                case 7026:
                    return isFluidEnabledForSource("MailCompose", mailboxInfo, LoopType.VideoRecord);
                case 7029:
                    return (isGlobalFeatureEnabled("cmp-dictation") &&
                        (isBrowserChrome() || isBrowserEDGECHROMIUM() || isBrowserFirefox()));
                case 7001:
                    return isCapabilitySupported(editorCapability, mailboxInfo);
                case 7040:
                    return shouldShowTemplateControl(mailboxInfo);
                case 7035:
                    return shouldShowSaveAsControl(mailboxInfo);
                case 7049:
                    return isGlobalFeatureEnabled("mon-recipient-addressbookinribbon");
                case 7050:
                    return isGlobalFeatureEnabled("mon-recipient-checknamesinribbon");
                case 7054:
                    return isCapabilityEnabled(quickPartsCapability, mailboxInfo);
                
                default:
                    return true;
            }
        
        case 11:
            switch (controlId) {
                case 7069:
                    return shouldShowComposeFollowUp(composeProps);
                case 7012:
                    return shouldShowEncrypt(props);
                case 564:
                    return shouldShowSensitivity(props);
                case 7010:
                    return !getIsConsumer(mailboxInfo);
                case 7013:
                    return shouldShowSendLater(props);
                case 7015:
                    return sholdShowDeliveryReceipt(props);
                case 7001:
                    return isCapabilitySupported(editorCapability, mailboxInfo);
                case 7040:
                    return shouldShowTemplateControl(mailboxInfo);
                case 7035:
                    return shouldShowSaveAsControl(mailboxInfo);
                
                default:
                    return true;
            }
        
        case 12:
            return true;
        
        case 13:
            switch (controlId) {
                default:
                    return true;
            }
        
        case 14:
            switch (controlId) {
                case 739:
                    return false;
                case 766:
                    return isGlobalFeatureEnabled("sea-searchRibbon-highlightToggle");
                case 770:
                    return (mailSearchStore().provider !== SearchProvider.Offline &&
                        canToggleSearchIncludeDeleted(mailSearchStore().staticSearchScope, mailSearchStore().selectedMailboxInfo));
                case 741:
                    return shouldShowSearchRefiner("HasAttachment");
                case 742:
                    return shouldShowSearchRefiner("IsUnread");
                case 743:
                    return shouldShowSearchRefiner("IsMentioned");
                case 745:
                    return shouldShowSearchRefiner("IsFlagged");
                case 746:
                    return shouldShowSearchRefiner("HighImportance");
                case 771:
                    return isGlobalFeatureEnabled("sea-searchRibbon-fromRefiner");
                case 773:
                    return isGlobalFeatureEnabled("sea-searchRibbon-toRefiner");
                case 747:
                    return (shouldShowSearchRefiner("ToMe") && !isMultiAccountTable(selectedTableView));
                case 748:
                    return (isGlobalFeatureEnabled("sea-searchRibbon-dateRefiner") &&
                        !isOfflineOrPSTSearch(selectedTableView));
                case 749:
                    return (shouldShowSearchRefiner("IncludeChannels") &&
                        !isGlobalFeatureEnabled("sea-deprecateSerpTabs"));
                case 744:
                    return (shouldShowSearchRefiner("IsMentionedInChats") &&
                        !isGlobalFeatureEnabled("sea-deprecateSerpTabs"));
                case 754:
                case 753:
                case 750:
                    return (!isUnifiedInbox &&
                        !isOfflineOrPSTSearch(selectedTableView) &&
                        !isGlobalFeatureEnabled("sea-deprecateSerpTabs"));
                case 751:
                    return (!isOfflineOrPSTSearch(selectedTableView) &&
                        getIsTeamsChatTabEnabled(mailboxInfo) &&
                        !isGlobalFeatureEnabled("sea-deprecateSerpTabs"));
                case 752:
                    return (!isUnifiedInbox &&
                        !isOfflineOrPSTSearch(selectedTableView) &&
                        !isGlobalFeatureEnabled("sea-disable-searchTabs-files") &&
                        !isGlobalFeatureEnabled("sea-deprecateSerpTabs"));
                default:
                    return true;
            }
        
        case 15:
            // Tab visibility is gated by the 'cmp-mail-merge-v2' flight in composeTabUtils.ts
            return true;
        
        // These tabs are only added to the MLR and SLR definitions when Win32 styling is enabled,
        // so it is safe to return true here as the default
        case 17:
            switch (controlId) {
                case 768:
                    return !isUnifiedInbox;
                case 769:
                    return (!isUnifiedInbox &&
                        getEnterpriseOrPremiumLicenseStatus() == "HasEnterpriseOrPremiumLicense" &&
                        isCapabilityEnabled(searchFoldersCapability, mailboxInfo));
                case 767:
                    return isCapabilitySupported(favoritesCapability, mailboxInfo);
                case 772:
                    return (!isUnifiedInbox &&
                        !isConsumer(undefined /* smtpAddress */, mailboxInfo) &&
                        !isLocalDataBackedMailboxInfo(mailboxInfo));
                default:
                    return true;
            }
        
        case 16:
            switch (controlId) {
                case 562:
                    return !isLocalDataBackedMailboxInfo(mailboxInfo);
                default:
                    return true;
            }
        
        default: {
            const error: TraceErrorObject = new Error("getShouldShowControlsRules.ts: Unknown tab id");
            error.additionalInfo = { tabId };
            throw error;
        }
    }
});

function hasSpamReportAddin(mailboxInfo: MailboxInfo): boolean {
    // Check if the spam addin is installed from extensibility state
    return (isGlobalFeatureEnabled("addin-enableIntegratedSpam") && isSpamAddinInstalled(mailboxInfo));
}

function shouldShowComposeFollowUp(props: ComposeRibbonControlDefProps): boolean {
    const viewState = props.composeViewState;
    return (!!viewState &&
        isGlobalFeatureEnabled("cmp-sender-follow-up") &&
        isCapabilityEnabled(advancedFlagCapability, viewState.mailboxInfo) &&
        isCapabilitySupported(serviceRequestCapability, viewState.mailboxInfo) &&
        !isLocalDataBackedMailboxInfo(viewState.mailboxInfo) &&
        !isMessageFileMailboxInfo(viewState.mailboxInfo) &&
        !isGroupComposeViewState(viewState) &&
        !isPublicFolderComposeViewState(viewState) &&
        !viewState.mailMergeViewState.isMailMerge);
}

/**
 * Checks if native reporting (Junk/Phishing) is enabled for a specific mailbox,
 * considering both native reporting support and spam report addin presence.
 */
export function isReportingEnabledForMailbox(mailboxInfo: MailboxInfo): boolean {
    return !hasSpamReportAddin(mailboxInfo) && isNativeReportingEnabled(mailboxInfo);
}

function shouldShowStandaloneReportControl(mailboxInfo: MailboxInfo, selectedTableView: TableView | null): boolean {
    if (!isGlobalFeatureEnabled("tri-promote-not-junk")) {
        return false;
    }
    
    // Keep the mailbox-type restrictions (group, archive, local data) the Report split enforced.
    if (getMailMenuItemShouldHideForMailbox(24, mailboxInfo)) {
        return false;
    }
    
    if (isUnifiedInboxMailboxInfo(mailboxInfo)) {
        return isAnyUnifiedInboxAccountReportingEnabled(mailboxInfo);
    }
    
    if (isMultiAccountTable(selectedTableView ?? undefined)) {
        return true;
    }
    
    return isReportingEnabledForMailbox(mailboxInfo);
}

/**
 * For unified inbox, check if any of the constituent accounts supports native reporting.
 * The unified inbox mailboxInfo itself is a virtual mailbox and doesn't support service requests,
 * so we check the individual accounts instead.
 */
function isAnyUnifiedInboxAccountReportingEnabled(mailboxInfo: MailboxInfo): boolean {
    const uibMailboxInfo = mailboxInfo as UnifiedInboxMailboxInfo;
    return uibMailboxInfo.accountsMailboxInfos.some(accountMailboxInfo => !hasSpamReportAddin(accountMailboxInfo) && isNativeReportingEnabled(accountMailboxInfo));
}

/**
 * Hide the Block split button for Unified Inbox or multi-account search unless the current
 * selection resolves to a single supported mailbox. Block/Never Block are flyout children, so
 * hiding the split button hides them.
 */
function isBlockSupportedForUnifiedInboxOrMultiAccountSearchRibbon(isUnifiedInbox: boolean, mailboxInfo: MailboxInfo): boolean {
    const blockTableView = getSelectedTableView();
    if (!isUnifiedInbox && !isMultiAccountTable(blockTableView)) {
        return true;
    }
    return (!!blockTableView &&
        isBlockSupportedForUnifiedInboxOrMultiAccountSearchSelection(mailboxInfo, [...blockTableView.selectedRowKeys.keys()], blockTableView));
}

function shouldShowFindTimeControl(isConsumerAcct: boolean): boolean {
    return isGlobalFeatureEnabled("cal-native-findtime") && !isConsumerAcct;
}

function shouldShowReplyByMeetingFlyout(mailboxInfo: MailboxInfo): boolean {
    return ((hasEnterpriseAccount() || isMessageFileMailboxInfo(mailboxInfo)) &&
        !!isCopilotFeatureEnabled("ReplyByMeeting", mailboxInfo) &&
        isGlobalFeatureEnabled("cal-copilot-schedule-from-email-bizchat"));
}

function shouldShowGetAddins(isConsumerAcct: boolean, ribbonTab: MailRibbonTabId, mailboxInfo: MailboxInfo): boolean {
    const shouldShowAddInButton = !isConsumerAcct &&
        isCapabilityEnabled(populateAddinsCapability, mailboxInfo) &&
        isCapabilityEnabled(addinsCapability, mailboxInfo) &&
        !isMessageExtensionsFlyoutEnabled(mailboxInfo) &&
        !isSharedMailboxExplicitLogon(mailboxInfo);
    
    if (ribbonTab === 1 ||
        ribbonTab === 10 ||
        ribbonTab === 5) {
        return shouldShowAddInButton && !shouldShowGetM365Apps(mailboxInfo);
    }
    
    return shouldShowAddInButton;
}

function shouldShowGetM365Apps(mailboxInfo?: MailboxInfo) {
    return (isMOS3AppServiceAppsInMailAvailable(mailboxInfo) &&
        isGlobalFeatureEnabled("mos-inContextStore"));
}

function shouldShowGetMessageExtensions(mailboxInfo?: MailboxInfo): boolean {
    return isMessageExtensionsFlyoutEnabled(mailboxInfo);
}

function shouldShowPolls(mailboxInfo: MailboxInfo): boolean {
    return (isGlobalFeatureEnabled("cmp-fluid-chapterThree") &&
        isFluidEnabledForSource("MailCompose", mailboxInfo, LoopType.Poll));
}

function showShouldLoopComponent(mailboxInfo: MailboxInfo): boolean {
    return isFluidEnabledForSource("MailCompose", mailboxInfo, LoopType.Fluid);
}

const shouldShowEncrypt = owaComputedFn(function shouldShowEncrypt(props: RibbonControlDefinitionProps): boolean {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const { RmsV2UIEnabled } = getUserConfiguration(); // indicates whether SimplifiedClientAccessEnabled is true
    return !!(RmsV2UIEnabled &&
        isEncryptionEnabled(mailboxInfo) &&
        isEncryptionTemplateAvailable(mailboxInfo));
});

/**
 * Display sensitivity label dropdown only if user is not a consumer. In case of consumer, we hide the sensitivity label button.
 * Future TO-DO: Add license check for mail workload
 *                  and modify return statement to display dropdown list on the basis of license check as well.
 */
const shouldShowSensitivity = owaComputedFn(function shouldShowSensitivity(props: RibbonControlDefinitionProps): boolean {
    return !isConsumer(undefined /* smtpAddress */, getRibbonMailboxInfo(props.mailboxInfo));
});

function shouldShowAttachFile(props: RibbonControlDefinitionProps, isCustomizer: boolean) {
    props = props as ComposeRibbonControlDefProps;
    return (isCustomizer ||
        (props.attachmentPickers != undefined && props.composeViewState != undefined));
}

function shouldShowSendLater(props: RibbonControlDefinitionProps): boolean {
    props = props as ComposeRibbonControlDefProps;
    const viewState = props.composeViewState;
    if (viewState) {
        return (!!getUserConfiguration().AsyncSendEnabled &&
            !isGroupComposeViewState(viewState) &&
            !isPublicFolderComposeViewState(viewState) &&
            /* eslint-disable-next-line no-restricted-properties  -- (https://aka.ms/OWALintWiki)
             * Adding IsShadowMailbox to restricted properties/methods.
             *	> 'IsShadowMailbox' is restricted from being used. IsCloudCache/IsShadowMailbox should be resolved in ECS as a filter in a feature flight if possible. */
            !getAccountScopeUserSettings(viewState.mailboxInfo).SessionSettings?.IsShadowMailbox);
    }
    return false;
}

function sholdShowDeliveryReceipt(props: RibbonControlDefinitionProps): boolean {
    props = props as ComposeRibbonControlDefProps;
    const viewState = props.composeViewState;
    if (viewState) {
        /* eslint-disable-next-line no-restricted-properties  -- (https://aka.ms/OWALintWiki)
         * Adding IsShadowMailbox to restricted properties/methods.
         *	> 'IsShadowMailbox' is restricted from being used. IsCloudCache/IsShadowMailbox should be resolved in ECS as a filter in a feature flight if possible. */
        return !getAccountScopeUserSettings(viewState.mailboxInfo).SessionSettings?.IsShadowMailbox;
    }
    return false;
}

function getIsConsumer(mailboxInfo: MailboxInfo): boolean {
    return isConsumer(undefined /* smtpAddress */, mailboxInfo);
}

function shouldShowTrackingControlsOnReadTab(props: RibbonControlDefinitionProps) {
    const clientItemId = (props as ReadOnlyRibbonControlDefProps).itemId;
    const item = getItem(clientItemId);
    if (item) {
        // Do not show if the item in not in sent items folder or if the item class is a Meeting
        return (isItemInFolder(item, [sentItemsFolderId]) &&
            item.ItemClass?.indexOf("IPM.Schedule.Meeting") == -1);
    }
    return false;
}

export function shouldShowShareToTeamsChat(mailboxInfo: MailboxInfo): boolean {
    return (isGlobalFeatureEnabled("mon-teams-ShareToTeamsChat") &&
        isTeamsChatEnabledWithDefault(mailboxInfo) &&
        (!isGlobalFeatureEnabled("settings-hideDisabledShareToTeams") ||
            isShareToTeamsChatEnabledByAdmin(mailboxInfo)));
}

function isFileActivationProjectionForProps(props: RibbonControlDefinitionProps): boolean {
    const ribbonProps = props as ReadOnlyRibbonControlDefProps;
    return isFileActivationProjection(ribbonProps.itemId, ribbonProps.projectionTabId);
}

function shouldShowSearchRefiner(refinerName: string): boolean {
    const suggestedRefiners = getSuggestedRefiners(SearchScenarioId.Mail);
    const suggestedRefiner = suggestedRefiners.find(refiner => refiner.name === refinerName);
    
    const appliedRefiners = getAppliedRefiners(SearchScenarioId.Mail);
    const appliedRefiner = appliedRefiners.find(refiner => refiner.name === refinerName);
    
    return !!(suggestedRefiner || appliedRefiner);
}

function shouldShowTemplateControl(mailboxInfo: MailboxInfo): boolean {
    return isCapabilityEnabled(mailTemplatesCapability, mailboxInfo);
}

/**
 * The Save As ribbon button should be shown only if at least one of the
 * file-format options that can appear in its flyout menu is enabled. The
 * set of options must be kept in sync with `getSaveAsFileSections` in the
 * `owa-mail-compose-control-sections` package.
 */
export function shouldShowSaveAsControl(mailboxInfo: MailboxInfo): boolean {
    return (isCapabilityEnabled(saveEmailAsEMLToFileCapability, mailboxInfo) ||
        isCapabilityEnabled(saveEmailAsMSGToFileCapability, mailboxInfo) ||
        isCapabilityEnabled(saveEmailAsOFTToFileCapability, mailboxInfo));
}

function isOfflineOrPSTSearch(selectedTableView: TableView): boolean {
    if (!selectedTableView || selectedTableView.tableQuery.type !== 1) {
        return false;
    }
    
    const searchTableQuery = selectedTableView.tableQuery as SearchTableQuery;
    const searchProvider = getSearchProvider(searchTableQuery.searchScope);
    
    return searchProvider === SearchProvider.Offline || searchProvider === SearchProvider.PST;
}

export function isShareToTeamsChatEnabledByAdmin(mailboxInfo: MailboxInfo): boolean {
    const shareToTeamsAppId = shareToTeamsGUIDs[0];
    const extension = getExtensionForAddinId(shareToTeamsAppId, mailboxInfo);
    return extension?.Enabled === true;
}
